'use client';

import { ComponentType, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { clearSession, getAccessToken, getSessionExpiresAt, renewSession } from '@/lib/auth/client';

/**
 * Module-level flag so the session check only blocks rendering on the first page load.
 * Subsequent client-side navigations skip the async check and render immediately.
 */
let sessionVerified = false;

/**
 * Timestamp (ms) of the last recorded user activity. Updated by pointer, keyboard,
 * and touch events on the document. Used to skip token renewal when the user is idle.
 */
let lastActivityAt = Date.now();

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'pointerdown'] as const;

function recordActivity() {
  lastActivityAt = Date.now();
}

/**
 * @method resetSessionVerified
 * @description Clears the in-memory session flag. Call on logout so the next
 * page load triggers a fresh auth check.
 * @returns Void.
 */
export function resetSessionVerified(): void {
  sessionVerified = false;
}

/**
 * @component withProtectedRoute
 * @description Wraps a client component with authenticated-session checks.
 * Blocks rendering only on the initial page load; subsequent navigations render instantly.
 * Proactively refreshes the token before expiry, but only when the user is active.
 * @param WrappedComponent - The component that should only render for authenticated users.
 * @returns A component that redirects to /login when session validation fails.
 */
export default function withProtectedRoute<P extends object>(WrappedComponent: ComponentType<P>) {
  /**
   * @method ProtectedRoute
   * @description Renders a route guard and the wrapped component after session validation succeeds.
   * @param props - Original wrapped-component props.
   * @returns Guarded component content or null while validating on first load.
   */
  return function ProtectedRoute(props: P) {
    const router = useRouter();
    const [isCheckingSession, setIsCheckingSession] = useState(!sessionVerified);
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (sessionVerified) return;

      const validateSession = async () => {
        const hasSession = await renewSession();
        if (!hasSession) {
          clearSession();
          router.replace('/login');
          return;
        }

        const token = getAccessToken();
        if (!token) {
          clearSession();
          router.replace('/login');
          return;
        }

        // Super admins are not allowed in the member portal — send them to /admin
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload?.app_metadata?.role === 'super_admin') {
            router.replace('/admin');
            return;
          }
        } catch {
          // Malformed token — fall through to the session API check which will reject it
        }

        const response = await fetch('/api/auth/session', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          clearSession();
          router.replace('/login');
          return;
        }

        sessionVerified = true;
        setIsCheckingSession(false);
      };

      void validateSession();
    }, [router]);

    // Track user activity so the refresh timer can skip renewal when idle.
    useEffect(() => {
      if (isCheckingSession) return;

      ACTIVITY_EVENTS.forEach((e) => document.addEventListener(e, recordActivity, { passive: true }));
      return () => {
        ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, recordActivity));
      };
    }, [isCheckingSession]);

    // Proactively refresh the token 5 minutes before it expires — only when active.
    useEffect(() => {
      if (isCheckingSession) return;

      const scheduleRefresh = () => {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }

        const expiresAt = getSessionExpiresAt();
        if (!expiresAt) return;

        // Fire 5 minutes (300 s) before expiry; never negative
        const msUntilRefresh = Math.max(0, expiresAt * 1000 - Date.now() - 5 * 60 * 1000);

        refreshTimerRef.current = setTimeout(async () => {
          // Skip renewal if the user has been idle since the last token was issued.
          // "Idle" = no activity recorded after the current token was issued (expiresAt - 1h).
          const tokenIssuedAt = (expiresAt - 3600) * 1000;
          const isIdle = lastActivityAt < tokenIssuedAt;

          if (isIdle) {
            // Let the token expire naturally — do not reschedule.
            return;
          }

          const ok = await renewSession(5 * 60);
          if (!ok) {
            clearSession();
            router.replace('/login');
            return;
          }

          const nextExpiresAt = getSessionExpiresAt();
          if (!nextExpiresAt) {
            clearSession();
            router.replace('/login');
            return;
          }

          // If the session was still outside the proactive threshold, renewSession returns true
          // without issuing new tokens. Avoid a 0 ms loop by waiting until actual expiry.
          if (nextExpiresAt <= expiresAt) {
            const msUntilExpiry = Math.max(0, nextExpiresAt * 1000 - Date.now());
            refreshTimerRef.current = setTimeout(async () => {
              const renewed = await renewSession(5 * 60);
              if (!renewed) {
                clearSession();
                router.replace('/login');
                return;
              }
              scheduleRefresh();
            }, Math.max(1000, msUntilExpiry));
            return;
          }

          // Reschedule for the next token's expiry.
          scheduleRefresh();
        }, msUntilRefresh);
      };

      scheduleRefresh();
      return () => {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      };
    }, [isCheckingSession, router]);

    if (isCheckingSession) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
}
